package org.catharsis.spendtel.feature.presentation

import androidx.lifecycle.viewModelScope
import io.github.aakira.napier.Napier
import kotlinx.coroutines.launch
import org.catharsis.spendtel.core.api.PaymentRequiredException
import org.catharsis.spendtel.core.api.SpendTelService
import org.catharsis.spendtel.core.arch.MviViewModel
import org.catharsis.spendtel.core.arch.ViewEffect
import org.catharsis.spendtel.core.arch.ViewEvent
import org.catharsis.spendtel.core.arch.ViewState
import org.catharsis.spendtel.feature.data.ExpenseReportResponse
import org.catharsis.spendtel.feature.data.StoragePathInput

class SpendTelServiceViewModel(
    private val spendTelService: SpendTelService,
) : MviViewModel<SpendTelServiceEvent, SpendTelServiceState, SpendTelServiceEffect>(
    initialState = SpendTelServiceState(),
) {
    override fun receiveEvent(event: SpendTelServiceEvent) {
        when (event) {
            is SpendTelServiceEvent.OnCreateExpenseReport -> createExpenseReport(event.storagePaths)
        }
    }

    private fun createExpenseReport(storagePaths: List<StoragePathInput>) {
        launch {
            setState { copy(isLoading = true) }
            spendTelService.createExpenseReport(storagePaths)
                .onSuccess { response ->
                    Napier.d("Expense report created: ${response.reportId}")
                    setState { copy(isLoading = false) }
                    emitEffect { SpendTelServiceEffect.OnExpenseReportCreated(response) }
                }
                .onFailure { error ->
                    Napier.e("Expense report creation failed: $error")
                    setState { copy(isLoading = false) }
                    val message = when (error) {
                        is PaymentRequiredException -> error.paymentError.message
                        else -> error.message ?: "Could not start expense report analysis."
                    }
                    emitEffect { SpendTelServiceEffect.OnError(message = message) }
                }
        }
    }
}

data class SpendTelServiceState(
    val isLoading: Boolean = false,
) : ViewState

sealed class SpendTelServiceEvent : ViewEvent {
    data class OnCreateExpenseReport(
        val storagePaths: List<StoragePathInput>,
    ) : SpendTelServiceEvent()
}

sealed class SpendTelServiceEffect : ViewEffect {
    data class OnExpenseReportCreated(
        val response: ExpenseReportResponse,
    ) : SpendTelServiceEffect()

    data class OnError(val message: String) : SpendTelServiceEffect()
}
